import { FullStory, isInitialized } from '@fullstory/browser';
import { logger } from '~/logger/logger';

export type FSProperty =
  | string
  | number
  | boolean
  | Date
  | string[]
  | number[]
  | boolean[]
  | Date[];

export type FSProperties = Record<string, FSProperty>;

const MAX_RETRIES = 5;
const RETRY_INTERVAL = 1000;

function sendFullstoryEvent(event: string, properties?: FSProperties) {
  try {
    const eventData = properties ? { ...properties } : {};
    FullStory('trackEvent', { name: event, properties: { ...eventData } });
  } catch (e) {
    logger.log('Error calling Fullstory event', e);
  }
}

/**
 * Fullstory events should be called in the BE where possible, but sometimes there's not an API call to hook into.
 * If you need to call a Fullstory event from the FE, use this function.
 *
 * @param event the name of the event
 * @param properties a hash of properties to attach to the event
 */
export async function fullstoryEvent(event: string, properties?: FSProperties) {
  // If Fullstory is not initialized, intermittently check for initialization up to MAX_RETRIES times
  let retries = 0;
  while (!isInitialized() && retries < MAX_RETRIES) {
    retries++;
    await new Promise((resolve) => setTimeout(resolve, RETRY_INTERVAL));
  }

  // Send event if Fullstory is initialized, otherwise log a warning
  if (isInitialized()) {
    sendFullstoryEvent(event, properties);
  } else {
    logger.warn(
      `FullStory failed to initialize after ${retries} attempts. Event '${event}' dropped.`
    );
  }
}
